import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskSlug } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { useMatchContainer } from '@sorare/core/src/hooks/ui/useMatchContainer';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { commonPacksContainerQuery } from 'components/commonCard/CommonPacksPage/utils';
import { usePackAssets } from 'components/commonCard/PacksTypePage/usePackAssets';

import { DailyClaimModule } from '../DailyClaimModule';
import { PackWithBuyButton } from '../PackWithBuyButton';
import { useItemToDisplay } from '../useItemToDisplay';
import type { NewDailyDeals_marketRoot } from './__generated__/index.graphql';

const Root = styled(Container)`
  background:
    linear-gradient(to bottom, transparent 77%, var(--c-black) 100%),
    var(--c-background-image) bottom center/100% 100% no-repeat;
  gap: var(--double-unit);
  padding: var(--triple-unit) 0;
  @container ${commonPacksContainerQuery} {
    padding: var(--triple-unit);
  }
  justify-content: stretch;
`;

const PacksList = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: space-around;
  width: 100%;
  gap: var(--quadruple-unit);
`;
const PacksWrapper = styled(SBHorizontal)`
  gap: var(--quadruple-unit);
  flex: 1;
  min-width: min-content;
  max-width: 50%;
`;
const DailyClaimModuleWrapper = styled.div`
  width: 100%;
  @container ${commonPacksContainerQuery} {
    width: max-content;
  }
`;
const ScrollableWrapper = styled.div`
  width: 100%;
`;
type Props = {
  marketRoot: NewDailyDeals_marketRoot;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
  refetch: () => Promise<unknown>;
  balances: React.ReactNode;
};
export const NewDailyDeals = ({
  marketRoot,
  onClickBuy,
  refetch,
  balances,
}: Props) => {
  const { cardPackGroups } = marketRoot;
  const { tasks } = marketRoot;
  const { ref: scrollableWrapperRef, itemToDisplay } = useItemToDisplay();
  const dailyDealCardPackGroup = cardPackGroups.find(
    cardPackGroup => cardPackGroup.dailyDeal
  );
  const { bgImage } = usePackAssets({
    cardPackGroupId: dailyDealCardPackGroup?.id || '',
  });
  const [matchContainerRefCallback, isWideContainer] = useMatchContainer(
    commonPacksContainerQuery
  );

  const dailyClaimTask = tasks
    .filter(isTypeFn('Task'))
    .find(task => task.name === ManagerProgressionTaskSlug.DAILY_CLAIM);

  if (!dailyDealCardPackGroup && !dailyClaimTask) {
    return null;
  }

  const dailyDealPacks = dailyDealCardPackGroup?.cardPacks || [];

  return (
    <Root
      style={{ '--c-background-image': `url(${bgImage})` } as CSSProperties}
      maxWidth={1600}
    >
      {balances}
      <Vertical gap={0.5}>
        <BrandM>
          <FormattedMessage {...glossary.dailyDeals} />
        </BrandM>
        <LabelL
          className="text-gradient-color"
          style={
            {
              '--text-gradient-color': 'var(--c-gradient-gold)',
            } as CSSProperties
          }
        >
          <FormattedMessage
            id="commonCard.DailyDeals.exclusivePacks"
            defaultMessage="Exclusive Packs"
          />
        </LabelL>
      </Vertical>
      <PacksList ref={matchContainerRefCallback}>
        {isWideContainer ? (
          <PacksWrapper>
            {dailyDealPacks.map(pack => {
              return (
                <PackWithBuyButton
                  key={pack.slug}
                  cardPack={pack}
                  cardPackGroupId={dailyDealCardPackGroup?.id || ''}
                  onClickBuy={onClickBuy}
                />
              );
            })}
          </PacksWrapper>
        ) : (
          <ScrollableWrapper ref={scrollableWrapperRef}>
            <Scrollable
              itemToDisplay={itemToDisplay}
              overhang={itemToDisplay === 1 ? 0 : 6}
            >
              {dailyDealPacks.map(pack => {
                return (
                  <PackWithBuyButton
                    key={pack.slug}
                    cardPack={pack}
                    cardPackGroupId={dailyDealCardPackGroup?.id || ''}
                    onClickBuy={onClickBuy}
                  />
                );
              })}
            </Scrollable>
          </ScrollableWrapper>
        )}
        {!!dailyClaimTask && (
          <DailyClaimModuleWrapper>
            <DailyClaimModule task={dailyClaimTask} refetch={refetch} />
          </DailyClaimModuleWrapper>
        )}
      </PacksList>
    </Root>
  );
};

NewDailyDeals.fragments = {
  marketRoot: gql`
    fragment NewDailyDeals_marketRoot on MarketRoot {
      id
      cardPackGroups(sport: $sport) {
        id
        dailyDeal
        cardPacks {
          slug
          ...PackWithBuyButton_cardPack
        }
      }
      tasks(sport: $sport) {
        id
        name
        ...DailyClaimModule_task
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
    ${DailyClaimModule.fragments.task}
  ` as TypedDocumentNode<NewDailyDeals_marketRoot>,
};
